/**
 * **抽牌堆** —— 一个 `HandBacks`（牌扇）的两种形态。
 *
 * | 状态 | 摆法 | 未知牌 |
 * | --- | --- | --- |
 * | 收起（常态） | **竖着一摞**：沿用并入牌扇之前的观感 —— 每张 y 错开 `COLLAPSED_PITCH`（快抽完时变大）、整叠居中、牌背随机偏几度 | 逐张画牌背（`compressPileRuns`），**不折叠、不盖数字** |
 * | 展开 | **横排序列**：固定间距、弧度 0、`maxWidth = ∞`（不按张数压缩），牌多时滚轮横向滚动 | 连续未知合成「未知牌叠」，>4 张盖牌数 |
 *
 * 外壳是一个**视口**：内容整体平移、套矩形遮罩。展开时滚轮把 `deltaY` 当横向滚。
 *
 * ## 塞回异常（拆弹）
 *
 * 多摆一张 [异常]，**强制拖拽**（`beginInsertDrag`）。插入位**不再看牌的
 * 实际槽位**，而是把视口横轴（左右各留一段 padding）**线性映射**到
 * `0..N` 每一个插入位：指针在哪一格，就选哪个位置。同时整个牌组平移，
 * 让选中的那一段正好落在指针下 —— 于是指针右移、牌组左移，
 * 无论牌堆多长，每一个位置都够得着（用户要求）。
 *
 * 松手按同一个映射提交（`commitInsert`）。
 *
 * ⚠️ 抽牌堆**不跟音乐脉动**（用户要求）—— 所以这里不暴露 `pulse()`，宿主也别调。
 */

import { Container, Graphics, Rectangle, type FederatedPointerEvent, type FederatedWheelEvent, type Renderer } from 'pixi.js';
import { faceSize } from '../card/metrics';
import type { CardDef } from '../../core/card';
import type { CardFaceCache } from '../card/faceCache';
import type { CardTitle } from '../../core/ids';
import { CardStrip, type StripItem } from './cardStrip';
import { toLocalBox, type HoverSource } from './cardTooltip';
import { getRenderCount, isNearlyEmpty, type RenderCountOptions } from './renderCount';
import { attachPan, type Pan } from '../ui/pan';

/** 展开时相邻两张的固定横向间距占牌宽的比例 */
const SPACING_SHARE = 0.5;
/**
 * 塞回异常时，视口左右各留的 padding（占牌宽的比例）。
 *
 * 映射出的插入位中心最靠边也要能把那张牌完整摆下，所以至少 0.5；
 * 取 0.6 留一点余量。
 */
const INSERT_SIDE_PAD_SHARE = 0.6;
/**
 * 收起（常态）时的**竖向堆叠**参数 —— 沿用并入牌扇之前的抽牌堆观感。
 *
 * 那时用 `stackLayout`：每张沿 y 错开一个固定的 pitch（快抽完时变大），
 * 整叠居中，牌背随机偏几度。这里用 `HandBacks(yOffset = -pitch)`
 * 复现同一个几何（`stackLayout.offsets[i] = -(i*pitch) + total/2` 与
 * `(place-(n-1)/2) * (-pitch)` 逐项相等）。
 */
const COLLAPSED_PITCH = 2;
const COLLAPSED_NEARLY_EMPTY_PITCH = 5;
const COLLAPSED_MAX_TILT_DEG = 3.4;

export interface DrawPileOptions {
  readonly renderer: Renderer;
  /** 牌**高**（逻辑像素）。宽度按卡牌比例算 */
  readonly height: number;
  /** 滚动视口的宽度（逻辑像素）。超过它的序列靠滚轮横滚 */
  readonly width: number;
  readonly seed?: number;
  /** 牌名 → 定义。画**已知**的那几张牌面用。不给就一律画牌背 */
  readonly defOf?: (title: CardTitle) => CardDef | undefined;
  /** 牌面纹理缓存（画已知牌面用）。不给则已知牌也画牌背 */
  readonly faces?: CardFaceCache;
  /**
   * 收全局指针事件的地方（一般就是 app.stage）。
   *
   * 塞回异常的拖拽要跨出自身范围（牌可能悬在别处），所以由**组件**自己挂
   * `globalpointermove` / `pointerup` —— 界面层只调方法，不碰指针事件。
   */
  readonly input?: Container;
}

export class DrawPile {
  /** 加到场景里的东西（视口）。原点在**视口中心**（调用方自己摆位置） */
  readonly view: Container;

  readonly #backs: CardStrip;
  readonly #content: Container;
  readonly #card: ReturnType<typeof faceSize>;
  readonly #size: { readonly width: number; readonly height: number };
  /** 视口 / 遮罩的高度：比牌高一些，给 hover 抬起 + 放大留空间 */
  readonly #viewHeight: number;
  readonly #defOf: DrawPileOptions['defOf'];
  /** 最近一次 `setPile` 的牌面表 —— 收起/展开切换时要据此重建（数字显不显示） */
  #titles: readonly (CardTitle | null)[] = [];
  #pileOptions: RenderCountOptions = {};
  #hoverSlot: number | null = null;
  #scrollX = 0;
  /** 触控横滚（和滚轮同一根轴、同一套边界与回弹） */
  #pan: Pan | null = null;
  #totalWidth = 0;
  #expanded = false;
  #pickable = false;
  /** 塞回异常模式（点一下不提交，只走拖拽） */
  #insertMode = false;
  /** 塞回异常时多摆的那张 [异常]（null = 没有） */
  #insertLethal: CardTitle | null = null;
  #onInsert: ((index: number) => void) | null = null;
  #input: Container | null = null;
  #onPick: ((ratioX: number) => void) | null = null;
  /** 塞回拖拽时指针的**视口本地 x**（没在拖 = null）。横轴映射与平移都靠它 */
  #insertPointerX: number | null = null;
  /**
   * 塞回平移的**目标** `content.x`。真正的 `content.x` 每帧缓动过去。
   *
   * ⚠️ 以前是「指针一动就直接给 `content.x` 赋值」—— 插入位每跨一格，
   *    `gapX` 就跳一个牌距，牌组跟着**瞬移**一下，看着就是抽搐（用户报的）。
   *    改成缓动之后整组是滑过去的。
   */
  #insertPanTarget = 0;
  /** 当前选中插入位的牌扇本地 x（每帧定位被拖那张时要用） */
  #insertGap = 0;

  constructor(options: DrawPileOptions) {
    this.#card = faceSize(options.height);
    this.#size = { width: options.width, height: this.#card.height };
    this.#viewHeight = this.#card.height * 1.6;
    this.#defOf = options.defOf;

    this.#backs = new CardStrip({
      renderer: options.renderer,
      height: options.height,
      ...(options.faces === undefined ? {} : { faces: options.faces }),
      // ★ 固定间距：maxWidth 给 ∞，于是 min(maxSpread, ∞) = maxSpread，不随张数变
      maxSpread: 0,
      maxWidth: Number.POSITIVE_INFINITY,
      // ★ 展开是一条直线（弧度 0、倾角 0）；收起是竖着一摞（见 `#applyPile`）
      curvature: 0,
      maxTilt: COLLAPSED_MAX_TILT_DEG,
      randomRotation: true,
      // ★ 可 hover（pickable）；牌面出血交给 CardStrip 按"面/背"决定
      interactive: true,
      pickable: true,
      // hover 到某一（已知）张 → 记下来，`hoverSource()` 给 tooltip 用
      onHover: (slot) => {
        this.#hoverSlot = slot;
      },
      ...(options.defOf === undefined ? {} : { defOf: options.defOf }),
    });

    this.#content = new Container();
    this.#content.label = 'draw-pile-content';
    // ⚠️ 牌面是从 (0,0) 往下画的，所以把内容整体上移半个牌高，
    //    让视口原点正好在牌心 —— 否则遮罩会切掉牌的下半部分（看起来是黑的）
    this.#content.position.set(0, -this.#size.height / 2);
    this.#content.addChild(this.#backs.view);

    this.view = new Container();
    this.view.label = 'draw-pile';
    this.view.addChild(this.#content);

    const W = this.#size.width;
    const H = this.#viewHeight;
    const maskShape = new Graphics().rect(-W / 2, -H / 2, W, H).fill({ color: 0xffffff });
    this.view.addChild(maskShape);
    this.#content.mask = maskShape;

    this.view.eventMode = 'static';
    this.#applyHitArea();
    // 滚轮横滚：deltaY 当横向位移用（用户："此时令滚轮可以横向滚动序列"）
    this.view.on('wheel', (event: FederatedWheelEvent) => {
      event.preventDefault?.();
      this.#scrollTo(this.#scrollX - event.deltaY);
    });
    // 插入位置命中：按 x 比例（0 = 最左 = 牌堆底，1 = 最右 = 牌堆顶）
    // ★ 塞回异常的拖拽：牌可能悬在别处，所以挂到调用方给的**全局**输入层。
    //   界面层不碰指针事件（design.test.ts 那条硬规矩）。
    if (options.input !== undefined) {
      this.#input = options.input;
      this.#input.on('globalpointermove', this.#onGlobalMove);
      this.#input.on('pointerup', this.#onGlobalUp);
      this.#input.on('pointerupoutside', this.#onGlobalUp);
    }

    this.view.on('pointertap', (event: FederatedPointerEvent) => {
      // 刚拖动过（触控横滚）就不要再选牌：Pixi 的 tap 不看到底移了多远
      if (this.#pan?.justDragged() === true) return;
      // 塞回异常走拖拽（见 `dragInsertAt` / `commitInsert`），点一下不提交
      if (this.#insertMode) return;
      if (!this.#pickable || this.#onPick === null) return;
      const local = this.view.toLocal(event.global);
      const ratio = Math.min(1, Math.max(0, (local.x - this.#scrollX + W / 2) / W));
      this.#onPick(ratio);
    });

    // 触控横滚：内容整体平移，边界和滚轮一样是「两端刚好进视口」。
    // 塞回异常时不滚 —— 那时指针被那张 [异常] 的拖拽占着。
    this.#pan = attachPan({
      surface: this.view,
      axis: 'x',
      bounds: () => {
        const max = Math.max(0, (this.#totalWidth - this.#size.width) / 2);
        return { min: -max, max };
      },
      read: () => this.#content.x,
      write: (value) => {
        this.#scrollX = value;
        this.#content.x = value;
      },
      enabled: () => !this.#insertMode,
    });
  }

  /** 现在渲染了几张（诊断 / 测试用） */
  get rendered(): number {
    return this.#backs.rendered;
  }

  /** 视口尺寸（调用方排版用） */
  get size(): { readonly width: number; readonly height: number } {
    return { width: this.#size.width, height: this.#size.height };
  }

  /** 内容实际有多宽（排版「常态在右上角」时用它把右边缘对齐） */
  get contentWidth(): number {
    return this.#totalWidth;
  }

  /** 内容现在的横向平移（塞回平移的读数 / 测试用） */
  get contentX(): number {
    return this.#content.x;
  }

  /** 抽牌堆里现在被 hover 的那张（已知）牌 —— 给 tooltip 用。没有就是 null */
  hoverSource(): HoverSource | null {
    // ★ 塞回拖拽期间**一律不弹悬停说明**：被拖那张 [异常] 就在指针底下，
    //   它的说明会盖住下方的"塞回位置"提示（用户）。拖拽本身已经强制、
    //   指针也不停在动，这时悬停说明本来也读不了。
    if (this.#insertMode && this.#backs.dragging) return null;
    const slot = this.#hoverSlot;
    if (slot === null) return null;
    const title = this.#backs.titleAt(slot);
    const def = title === null ? undefined : this.#defOf?.(title);
    const view = this.#backs.slotView(slot);
    if (def === undefined || view === null) return null;
    return {
      hoveredDef: def,
      // `getBounds()` 是世界坐标，换算回逻辑坐标再交给说明面板
      hoveredBox: () => {
        const bounds = view.getBounds();
        const frame = this.view.parent;
        return frame === null ? bounds : toLocalBox((point) => frame.toLocal(point), bounds);
      },
    };
  }

  /**
   * **收起 / 展开。** 收起来时间距很小（叠成一小摞，常驻右上角）；
   * 展开时用固定的大间距（横排序列，居中在屏幕中心上方、可滚轮横滚）。
   */
  setExpanded(expanded: boolean): void {
    if (this.#expanded === expanded) return;
    this.#expanded = expanded;
    this.#applyPile();
  }

  /** 塞回异常：让 [异常] 作为牌扇最右（堆顶）的一张一起摆出来 */
  setInsertLethal(title: CardTitle | null): void {
    if (this.#insertLethal === title) return;
    this.#insertLethal = title;
    this.#applyPile();
  }

  /** 现在那张 [异常] 被强制拖着吗 */
  get insertDragging(): boolean {
    return this.#backs.dragging;
  }

  /** 松手时把落点（0..drawPile.length）交给界面层 */
  setOnInsert(handler: (index: number) => void): void {
    this.#onInsert = handler;
  }

  /**
   * **此刻被拖那张 [异常] 的落点插入位**（0..drawPile.length）。
   *
   * 不在拖拽态 / 还没有指针位置时返回 -1。和 `commitInsert` 用**同一个**
   * 横轴映射，所以提示面板显示的和松手后提交的必然一致。
   */
  insertIndexNow(): number {
    if (!this.#backs.dragging) return -1;
    const x = this.#insertPointerX;
    return x === null ? -1 : this.#screenToIndex(x);
  }

  /**
   * **被拖那张 [异常] 现在是哪个矩形**（相对本视口的父层）。
   *
   * 提示面板就挂在它正下方。取不到（没在拖 / 没渲染）返回 null。
   */
  insertBox(): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null {
    if (!this.#backs.dragging) return null;
    const view = this.#backs.slotView(this.#backs.count - 1);
    if (view === null) return null;
    const frame = this.view.parent;
    const bounds = view.getBounds();
    return frame === null ? bounds : toLocalBox((point) => frame.toLocal(point), bounds);
  }

  /** 强制拖起那张 [异常]。初始先摆在视口正中，玩家一动就跟着走 */
  beginInsertDrag(): void {
    if (this.#insertLethal === null) return;
    this.#backs.beginDrag(this.#backs.count - 1);
    this.#backs.setGapOffset(false);
    this.#insertPointerX = 0;
    this.#applyInsertPan();
    // 第一帧直接落位（不然会从默认位置滑过去、像多动了一下）
    this.#content.x = this.#insertPanTarget;
    this.#backs.dragTo(this.#insertCursorX(0) - this.#content.x);
  }

  /** 指针（本视口坐标）拖到哪 —— 只更新平移**目标**，缓动交给每帧的 `update` */
  dragInsertAt(localX: number): void {
    if (!this.#backs.dragging) return;
    this.#insertPointerX = localX;
    this.#applyInsertPan();
  }

  /** 松手：返回插入位（0..drawPile.length），并收起拖拽态 */
  commitInsert(): number {
    if (!this.#backs.dragging) return -1;
    const x = this.#insertPointerX;
    const index = x === null ? -1 : this.#screenToIndex(x);
    this.#backs.endDrag();
    this.#insertPointerX = null;
    return index;
  }

  /** 中途取消（状态变了）：不提交，收回 */
  cancelInsert(): void {
    if (!this.#backs.dragging) return;
    this.#backs.endDrag();
    this.#backs.setGapOffset(true);
    this.#insertPointerX = null;
    this.#scrollTo(this.#scrollX);
  }

  /**
   * **视口横轴 → 插入位。** 两种情形：
   *
   * - **牌堆比映射范围宽**（要平移）：左右各留 `INSERT_SIDE_PAD_SHARE × 牌宽`
   *   的 padding，中间**线性**分成 `0..N` —— 指针最左 = 最底下、最右 = 最顶上，
   *   牌组跟着把选中的那一段滑到指针下。
   * - **牌堆全在视口里**（不平移）：直接用**牌堆实际的那几个间隙** —— 指针离
   *   哪一段最近就是哪一段。线性映射在这里会和静态间隙对不上，指针在 x、
   *   被拖那张却吸到别处，UI 和输入脱节（用户报的）。
   *
   * ⚠️ 和 `commitInsert` 必须走同一个函数，否则提示面板会和实际落点对不上。
   */
  #screenToIndex(x: number): number {
    const n = this.#titles.length; // 插入位有 n+1 个：0..n
    if (n <= 0) return 0;

    if (this.#fitsOnScreen()) {
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i <= n; i++) {
        const gap = this.#insertGapX(i);
        if (gap === null) continue;
        const dist = Math.abs(this.#content.x + gap - x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    const left = this.#insertLeft();
    const right = this.#insertRight();
    const usable = Math.max(1, right - left);
    const clamped = x < left ? left : x > right ? right : x;
    return Math.round(((clamped - left) / usable) * n);
  }

  /** 牌堆整条序列是否落在映射范围里（落在里面就不需要平移） */
  #fitsOnScreen(): boolean {
    return this.#totalWidth <= this.#insertRight() - this.#insertLeft();
  }

  /** 指针的**受夹 x**：进了 padding 就贴在那一段上，牌不会跑出屏幕 */
  #insertCursorX(x: number): number {
    const left = this.#insertLeft();
    const right = this.#insertRight();
    return x < left ? left : x > right ? right : x;
  }

  #insertLeft(): number {
    return -this.#size.width / 2 + this.#card.width * INSERT_SIDE_PAD_SHARE;
  }

  #insertRight(): number {
    return this.#size.width / 2 - this.#card.width * INSERT_SIDE_PAD_SHARE;
  }

  /** 插入位 `place` 在**牌扇本地**的 x（那一段的左边缘：第 place 张牌的左边） */
  #insertGapX(place: number): number | null {
    const center = this.#backs.slotCenterX(place);
    return center === null ? null : center - this.#card.width / 2;
  }

  /**
   * **算出牌组该滑到哪。** 指针右移 → 选中位次变大 → 那一段的本地 x 变大 →
   * `content.x` 目标变小（牌组相对左移），右侧的牌被拉进视口；反过来同理。
   *
   * ⚠️ 这里**只更新目标**，实际 `content.x` 在 `update()` 里缓动过去 ——
   *    插入位每跨一格 `gapX` 会跳一个牌距，直接赋值就是瞬移（抽搐）。
   */
  #applyInsertPan(): void {
    if (!this.#insertMode || !this.#backs.dragging) return;
    const px = this.#insertPointerX;
    if (px === null) return;
    const gap = this.#insertGapX(this.#screenToIndex(px));
    if (gap === null) return;
    this.#insertGap = gap;
    // ★ 牌堆总长比映射范围还短 → 整条序列本来就全在视口里，钉在中间不动
    //   （用户要求）。否则才跟着指针滑，把指针那一侧的牌拉进来。
    this.#insertPanTarget = this.#fitsOnScreen() ? 0 : this.#insertCursorX(px) - gap;
  }

  /** 开 / 关塞回异常模式（只影响"点一下不提交"的判定） */
  setInsertMode(on: boolean): void {
    if (this.#insertMode === on) return;
    this.#insertMode = on;
    // 进/出都从居中开始，免得沿用上一次的平移；间距规则也跟着模式走，所以要重画
    this.#scrollX = 0;
    this.#insertPointerX = null;
    if (!on) this.#backs.setGapOffset(true);
    this.#applyPile();
  }

  /** 最上面那张（序列最右端）相对视口中心的位置。这里就给中心 */
  get topOffset(): { readonly x: number; readonly y: number } {
    return { x: 0, y: 0 };
  }

  setPickable(pickable: boolean): void {
    this.#pickable = pickable;
    this.view.cursor = pickable ? 'pointer' : 'default';
  }

  setOnPick(handler: (ratioX: number) => void): void {
    this.#onPick = handler;
  }

  /**
   * **按位置重画这一叠。** `titles` 是**自底向上**的一串：
   * `null` = 未知（牌背），有牌名 = 已知（牌面）。长度 = 实际张数。
   * 每个位置**一张一项** —— 这就是"序列长度随牌量变长"的来源。
   */
  setPile(titles: readonly (CardTitle | null)[], options: RenderCountOptions = {}): void {
    this.#titles = titles;
    this.#pileOptions = options;
    this.#applyPile();
  }

  #applyPile(): void {
    if (this.#expanded) {
      // 展开：横排序列。已知画牌面、未知合成「未知牌叠」（>4 张才盖牌数）
      this.#backs.setMaxSpread(this.#card.width * SPACING_SHARE);
      this.#backs.setFanLook({ maxTilt: 0, randomRotation: false, yOffset: 0 });
      // ★ 每张一个槽、固定间距。不再把连续未知压成 0.2× 的"未知牌叠" ——
      //   那样它的内部牌会伸出去压住紧邻的已知牌（用户）。
      this.#backs.setItems(this.#withLethal(pileItems(this.#titles, true, this.#insertMode)));
    } else {
      // 收起：竖着一摞，**基本沿用并入牌扇之前的样子**
      //（每张 y 错开 pitch、整叠居中、牌背随机偏一点；未知也逐张画、不折叠）
      const nearlyEmpty = isNearlyEmpty(this.#titles.length, this.#pileOptions);
      const pitch = nearlyEmpty ? COLLAPSED_NEARLY_EMPTY_PITCH : COLLAPSED_PITCH;
      this.#backs.setMaxSpread(0);
      this.#backs.setFanLook({ maxTilt: COLLAPSED_MAX_TILT_DEG, randomRotation: true, yOffset: -pitch });
      this.#backs.setItems(this.#withLethal(pileItems(this.#titles, false, this.#insertMode)));
    }
    this.#totalWidth = this.#backs.width;
    if (this.#insertMode && this.#backs.dragging) {
      // ⚠️ 塞回拖拽中**不要**走 `#scrollTo` —— 它会用 `#scrollX` 覆盖掉
      //    正在缓动的 `content.x`，缓动就永远到不了目标。重画之后只更新
      //    平移目标（`slot.tx` 可能变了），当前位置保持不变。
      this.#applyInsertPan();
    } else {
      this.#scrollTo(this.#scrollX);
    }
    this.#applyHitArea();
  }

  /** 塞回异常时，把 [异常] 追加到牌扇最右（堆顶） */
  #withLethal(items: StripItem[]): StripItem[] {
    if (this.#insertLethal === null) return items;
    return [...items, { kind: 'card', title: this.#insertLethal }];
  }

  /** 兼容旧调用：全是未知的一叠 */
  setCount(actualCount: number, options: RenderCountOptions = {}): void {
    const count = Number.isFinite(actualCount) && actualCount > 0 ? Math.floor(actualCount) : 0;
    this.setPile(Array.from({ length: count }, () => null), options);
  }

  /** 宿主只拿得到 `deltaMS`（`TableChrome.updateFrame`），所以喂毫秒 */
  update(deltaMS: number): void {
    this.#stepInsertPan(deltaMS);
    this.#backs.step(deltaMS);
  }

  /**
   * **每帧把塞回平移缓动过去。**
   *
   * - `content.x` 指数缓动逼近 `#insertPanTarget`（时间常数约 90ms）：
   *   插入位跨格时不再瞬移，整组是滑过去的；
   * - 被拖的 [异常] 用**指针相对当前平移**定位，所以缓动期间它仍咬在指针下，
   *   不会跟着牌组飘走。
   */
  #stepInsertPan(deltaMS: number): void {
    if (!this.#insertMode || !this.#backs.dragging) return;
    const dt = Math.max(0, deltaMS);
    const k = dt <= 0 ? 1 : 1 - Math.exp(-dt / 90);
    const next = this.#content.x + (this.#insertPanTarget - this.#content.x) * k;
    this.#content.x = Math.abs(this.#insertPanTarget - next) < 0.05 ? this.#insertPanTarget : next;
    // 平移中：被拖那张咬住指针（目标 = 指针 x − gap，再加回 gap 就是指针 x）；
    // 不平移：目标 = 0，被拖那张正好落在选中那一段上。
    this.#backs.dragTo(this.#insertPanTarget + this.#insertGap - this.#content.x);
  }

  destroy(): void {
    this.#pan?.destroy();
    this.#pan = null;
    if (this.#input !== null) {
      this.#input.off('globalpointermove', this.#onGlobalMove);
      this.#input.off('pointerup', this.#onGlobalUp);
      this.#input.off('pointerupoutside', this.#onGlobalUp);
      this.#input = null;
    }
    this.view.destroy({ children: true });
  }

  #onGlobalMove = (event: FederatedPointerEvent): void => {
    if (!this.#backs.dragging) return;
    this.dragInsertAt(this.view.toLocal(event.global).x);
  };

  #onGlobalUp = (): void => {
    if (!this.#backs.dragging) return;
    const index = this.commitInsert();
    if (index >= 0) this.#onInsert?.(index);
  };

  /**
   * **命中区跟着内容走。** 展开时是整条视口（可滚、可点插入）；收起时只盖住
   * 那一小摞 —— 否则满屏宽的视口会在右上角拦住整个上半屏的点击。
   */
  #applyHitArea(): void {
    const H = this.#viewHeight;
    const w = this.#expanded
      ? this.#size.width
      : Math.max(this.#card.width, this.#totalWidth) + this.#card.width * 0.2;
    this.view.hitArea = new Rectangle(-w / 2, -H / 2, w, H);
  }

  /** 横向滚动：内容整体平移，夹在"两端刚好进视口"之间 */
  #scrollTo(x: number): void {
    this.#pan?.stop();
    const max = Math.max(0, (this.#totalWidth - this.#size.width) / 2);
    this.#scrollX = x < -max ? -max : x > max ? max : x;
    this.#content.x = this.#scrollX;
  }
}

/** **每个位置一张一项**（未知 → 牌背，已知 → 牌面）。抽牌堆这条横排用它 */
export function pileRowItems(titles: readonly (CardTitle | null)[]): StripItem[] {
  return titles.map((title) => ({ kind: 'card', title }) as StripItem);
}

/**
 * **收起态每张的 y 偏移**（相对整叠中心）。位次 0 在最下（y 最大）。
 *
 * 这正是并入牌扇之前 `stackLayout` 的公式；`HandBacks` 用
 * `(place - (n-1)/2) * yOffset` 加 `yOffset = -pitch` 得到同一串数。
 * 单拎出来是为了能在测试里**钉住"收起 = 过去那一摞"**（见 `drawPile.test.ts`）。
 */
export function collapsedStackOffsets(count: number, pitch: number = COLLAPSED_PITCH): readonly number[] {
  return Array.from({ length: count }, (_value, i) => ((count - 1) / 2 - i) * pitch);
}

/** 「未知牌叠」超过这个张数才盖数字标签 */
export const MIN_STACK_COUNT_LABEL = 4;

/** 未知牌叠是否盖数字标签（显式 showCount=false 可关掉） */
export function showsStackCount(count: number, showCount?: boolean): boolean {
  return showCount !== false && count > MIN_STACK_COUNT_LABEL;
}

/**
 * **自底向上的位置表 → 牌扇的项。**
 *
 * ⚠️ **不再有"未知牌叠"**（用户取消了这个概念）。连续未知牌是**逐张摆**的，
 * 只是：
 *
 * - 常态下：连续两张未知之间的**间距压到 0.2 倍**（gapScale）；
 * - **塞回异常时**（insertMode）：不再压缩，逐张等距摆 —— 插入位由
 *   屏幕横轴线性映射决定（见 `DrawPile` 的 `#screenToIndex`），不靠间距；
 * - 连续 **>4** 张未知牌，在**中间那张的上方**盖一个数字。
 */
export function pileItems(
  titles: readonly (CardTitle | null)[],
  showUnknownCount = true,
  insertMode = false,
): StripItem[] {
  const out: StripItem[] = [];
  let i = 0;
  while (i < titles.length) {
    const title = titles[i] ?? null;
    if (title !== null) {
      out.push({ kind: 'card', title });
      i += 1;
      continue;
    }
    let j = i;
    while (j < titles.length && (titles[j] ?? null) === null) j += 1;
    const run = j - i;
    const labelAt = i + Math.floor((run - 1) / 2);
    for (let k = i; k < j; k++) {
      out.push({
        kind: 'card',
        title: null,
        // 常态：这一段的第一张保持正常间距，后面每张压到 0.2 倍。
        // 塞回：不压缩，逐张等距。
        gapScale: insertMode ? 1 : k === i ? 1 : 0.2,
        label: showUnknownCount && showsStackCount(run) && k === labelAt ? String(run) : null,
      });
    }
    i = j;
  }
  return out;
}

/**
 * **把一串"自底向上"的位置按已知 / 未知分段，每段各自压缩。**
 *
 * 纯函数（不碰 Pixi）。`未知牌叠` 内部现在直接走 `getRenderCount`，
 * 这个函数保留下来钉住"每段分开压缩、已知段不丢"这条语义（测试用）。
 */
export function compressPileRuns(
  titles: readonly (CardTitle | null)[],
  options: RenderCountOptions = {},
): (CardTitle | null)[] {
  const out: (CardTitle | null)[] = [];
  let i = 0;
  while (i < titles.length) {
    const known = titles[i] !== null;
    let j = i;
    while (j < titles.length && (titles[j] !== null) === known) j += 1;
    const run = titles.slice(i, j);
    const want = getRenderCount(run.length, options);
    if (want > 0) {
      const picked = want >= run.length ? run : run.slice(run.length - want);
      out.push(...picked);
    }
    i = j;
  }
  return out;
}
